import { useState, useCallback, useMemo } from "react";
import {
  ItemOperation,
  ItemComponent,
  UpdateItemParams,
  Item,
  operationRowKey,
} from "@forgepoint/shared";
import { useItemsStore } from "./itemsStore";
import { BomRowTarget, filterTargetRow, replaceTargetRow } from "./bomEdits";
import BOMComponentRow from "./BOMComponentRow";
import BOMOperationRow from "./BOMOperationRow";
import BOMOperationSheet from "./BOMOperationSheet";
import BOMComponentSheet from "./BOMComponentSheet";
import { Button } from "@/components/ui/button";
import { Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { useOrganizationStore } from "@/store/organizationStore";

interface BOMBuilderProps {
  item: Item;
  quantities: string[];
  onUpdated?: () => void;
  depth?: number;
  /** When provided, clicking a component navigates within the parent sheet instead of opening a new one. */
  onNavigateToItem?: (itemId: string) => void;
  /** When true, operations and components are view-only. */
  locked?: boolean;
  /** Explains why operations and components are locked. */
  lockReason?: string;
  /** Changes after any save in this BOM tree, so every row's rolled-up cost refetches. */
  refreshKey?: number;
}

/**
 * Renders the BOM builder for operations and components.
 * Provides add/edit/remove functionality for item operations and components.
 */
export default function BOMBuilder({
  item,
  quantities,
  onUpdated,
  depth = 0,
  onNavigateToItem,
  locked = false,
  lockReason,
  refreshKey,
}: BOMBuilderProps) {
  const { itemLoadingStates, updateItem } = useItemsStore();
  const sequenceNumbersEnabled = useOrganizationStore(
    (state) => state.organization?.bomSequenceNumbersEnabled === true,
  );

  const [isAddOperationSheetOpen, setIsAddOperationSheetOpen] = useState(false);
  const [isAddComponentSheetOpen, setIsAddComponentSheetOpen] = useState(false);

  const loadingState = itemLoadingStates.get(item.id);
  const isSaving = loadingState?.isLoadingSave || false;

  const handleUpdateItem = useCallback(
    async (updates: UpdateItemParams) => {
      const { item: updatedItem } = await updateItem(item.id, updates);
      if (updatedItem) {
        toast.success("BOM updated successfully");
        onUpdated?.();
      } else {
        toast.error("Failed to update BOM");
      }
      return updatedItem !== null;
    },
    [item.id, updateItem, onUpdated],
  );

  /**
   * Builds a save's payload from the item as stored when it runs, so a sheet
   * never sends back a row another save already replaced.
   */
  const applyItemUpdate = useCallback(
    (buildUpdates: (current: Item) => UpdateItemParams): Promise<boolean> => {
      const current = useItemsStore.getState().items.get(item.id) ?? item;
      return handleUpdateItem(buildUpdates(current));
    },
    [item, handleUpdateItem],
  );

  const handleAddOperation = useCallback(
    (operation: ItemOperation): Promise<boolean> =>
      applyItemUpdate((current) => ({
        operations: [...current.operations, operation],
      })),
    [applyItemUpdate],
  );

  const handleSaveOperation = useCallback(
    (target: BomRowTarget, operation: ItemOperation): Promise<boolean> =>
      applyItemUpdate((current) => ({
        operations: replaceTargetRow(current.operations, target, operation),
      })),
    [applyItemUpdate],
  );

  const handleRemoveOperation = useCallback(
    (target: BomRowTarget): Promise<boolean> =>
      applyItemUpdate((current) => ({
        operations: filterTargetRow(current.operations, target),
        components: current.components.map((component) =>
          target.id && component.relatedItemOperationId === target.id
            ? { ...component, relatedItemOperationId: null }
            : component,
        ),
      })),
    [applyItemUpdate],
  );

  const handleAddComponent = useCallback(
    (component: ItemComponent): Promise<boolean> =>
      applyItemUpdate((current) => ({
        components: [...current.components, component],
      })),
    [applyItemUpdate],
  );

  const handleSaveComponent = useCallback(
    (target: BomRowTarget, component: ItemComponent): Promise<boolean> =>
      applyItemUpdate((current) => ({
        components: replaceTargetRow(current.components, target, component),
      })),
    [applyItemUpdate],
  );

  const handleRemoveComponent = useCallback(
    (target: BomRowTarget): Promise<boolean> =>
      applyItemUpdate((current) => ({
        components: filterTargetRow(current.components, target),
      })),
    [applyItemUpdate],
  );

  const handleNestedUpdate = useCallback(() => {
    onUpdated?.();
  }, [onUpdated]);

  const suggestedSequenceNumber = useMemo(
    () => getNextSequenceNumber(item.operations),
    [item.operations],
  );

  return (
    <>
      {/* Operations Section */}
      <>
        <tr>
          <td
            className="py-2 px-2 sm:px-4 bg-row-stripe relative sticky left-0 z-10 border-r border-border border-t border-t-border"
            data-field-anchor="operations"
          >
            {depth > 0 && (
              <div
                className="absolute inset-y-0 left-0 bg-card border-r border-border"
                style={{ width: `${Math.min(depth * 16, 48)}px` }}
              />
            )}
            <div
              className="flex items-center justify-between relative"
              style={{
                paddingLeft:
                  depth > 0 ? `${Math.min(depth * 16, 48)}px` : undefined,
              }}
            >
              <span className="font-semibold text-[10px] sm:text-xs text-foreground uppercase flex items-center gap-1.5">
                Operations
                {locked && <Lock className="h-3 w-3 text-amber-500" />}
              </span>
              {!locked && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsAddOperationSheetOpen(true)}
                  disabled={isSaving}
                  className="h-7 w-7 sm:h-8 sm:w-8 p-0 touch-manipulation"
                >
                  <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
              )}
            </div>
          </td>
          {quantities.map((_, i) => (
            <td
              key={i}
              className="py-2 px-2 sm:px-4 border-l border-border border-t border-t-border bg-muted/50"
            ></td>
          ))}
        </tr>
        {item.operations.map((op, index) => (
          <BOMOperationRow
            key={op.id ?? `${op.operationId}-${index}`}
            itemOperationId={op.id}
            operationId={op.operationId}
            rowKey={operationRowKey(op, index, item.operations)}
            sequenceNumber={op.sequenceNumber}
            showSequenceNumber={sequenceNumbersEnabled}
            setupTime={op.setupTime}
            runTimePerItem={op.runTimePerItem}
            qtyPer={op.qtyPer}
            runTimeFormat={op.runTimeFormat}
            efficiency={op.efficiency}
            overrideOperationCost={op.overrideOperationCost}
            overrideSetupRate={op.overrideSetupRate}
            minimumCharge={op.minimumCharge}
            quantityBreaks={op.quantityBreaks}
            quantities={quantities}
            reasoning={op.reasoning}
            depth={depth}
            onSave={(updatedOperation) =>
              handleSaveOperation(
                { id: op.id, index, rowCount: item.operations.length },
                updatedOperation,
              )
            }
            onRemove={
              locked
                ? undefined
                : () =>
                    handleRemoveOperation({
                      id: op.id,
                      index,
                      rowCount: item.operations.length,
                    })
            }
            disabled={locked}
            disabledReason={locked ? lockReason : undefined}
            onUpdated={onUpdated}
          />
        ))}
      </>

      <tr>
        <td
          className="py-2 px-2 sm:px-4 bg-row-stripe relative sticky left-0 z-10 border-r border-border border-t border-t-border"
          data-field-anchor="components"
        >
          {depth > 0 && (
            <div
              className="absolute inset-y-0 left-0 bg-card border-r border-border"
              style={{ width: `${Math.min(depth * 16, 48)}px` }}
            />
          )}
          <div
            className="flex items-center justify-between relative"
            style={{
              paddingLeft:
                depth > 0 ? `${Math.min(depth * 16, 48)}px` : undefined,
            }}
          >
            <span className="font-semibold text-[10px] sm:text-xs text-foreground uppercase flex items-center gap-1.5">
              Components
              {locked && <Lock className="h-3 w-3 text-amber-500" />}
            </span>
            {!locked && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAddComponentSheetOpen(true)}
                disabled={isSaving}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0 touch-manipulation"
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            )}
          </div>
        </td>
        {quantities.map((_, i) => (
          <td
            key={i}
            className="py-2 px-2 sm:px-4 border-l border-border border-t border-t-border bg-muted/50"
          ></td>
        ))}
      </tr>
      {item.components.map((comp, index) => (
        <BOMComponentRow
          key={comp.id ?? `${comp.itemId}-${index}`}
          component={comp}
          quantities={quantities}
          depth={depth}
          onSave={(updatedComponent) =>
            handleSaveComponent(
              { id: comp.id, index, rowCount: item.components.length },
              updatedComponent,
            )
          }
          onRemove={
            locked
              ? undefined
              : () =>
                  handleRemoveComponent({
                    id: comp.id,
                    index,
                    rowCount: item.components.length,
                  })
          }
          disabled={locked}
          disabledReason={locked ? lockReason : undefined}
          onUpdated={handleNestedUpdate}
          parentItemId={item.id}
          refreshKey={refreshKey}
          onNavigateToItem={onNavigateToItem}
          relatedOperationLabel={
            item.operations.find(
              (operation) => operation.id === comp.relatedItemOperationId,
            )?.description ?? undefined
          }
        />
      ))}

      {/* Add Operation Sheet */}
      <BOMOperationSheet
        open={isAddOperationSheetOpen}
        onOpenChange={setIsAddOperationSheetOpen}
        mode="add"
        onSave={handleAddOperation}
        suggestedSequenceNumber={suggestedSequenceNumber}
      />

      {/* Add Component Sheet */}
      <BOMComponentSheet
        open={isAddComponentSheetOpen}
        onOpenChange={setIsAddComponentSheetOpen}
        mode="add"
        onSave={handleAddComponent}
        parentItemId={item.id}
      />
    </>
  );
}

const SEQUENCE_NUMBER_STEP = 10;

/**
 * Suggests the sequence number for a newly added operation.
 * @param operations - The operations already on the item
 * @returns The next multiple of ten above the highest whole-number sequence number, "10" when none are numbered, or "" when the existing numbers are all non-numeric
 */
function getNextSequenceNumber(operations: ItemOperation[]): string {
  const assigned = operations.flatMap((operation) => {
    const value = operation.sequenceNumber?.trim();
    return value ? [value] : [];
  });
  if (assigned.length === 0) {
    return String(SEQUENCE_NUMBER_STEP);
  }

  const numeric = assigned
    .filter((value) => /^\d+$/.test(value))
    .map((value) => Number(value));
  if (numeric.length === 0) {
    return "";
  }

  const highest = Math.max(...numeric);
  return String(
    (Math.floor(highest / SEQUENCE_NUMBER_STEP) + 1) * SEQUENCE_NUMBER_STEP,
  );
}
